/* Push Reactivation Banner (storefront).

   Responsibilities:
   1. When Notification.permission === 'denied', render an educational
      banner that tells the visitor how to re-enable web push notifications
      for the current browser (Chrome, Firefox, Safari, Safari iOS, Edge).
   2. On "Don't show again", persist a 30-day TTL timestamp in
      localStorage[STORAGE_HIDE_KEY] so the banner stays hidden across
      sessions for that period.
   3. On "Accept", persist a session-scope flag in
      sessionStorage[SESSION_HIDE_KEY] so the banner stays hidden until
      the tab/browser is closed.
   4. On visibilitychange / focus, re-check Notification.permission. If
      the user flipped it from 'denied' to 'granted' via browser settings,
      remove the banner and reload the page so the existing
      firebase-push-client.js flow can register the FCM token on its own.
      This module does NOT call into firebase-push-client.js directly.

   i18n strings come from window.__HOLA_PUSH_REACTIVATION_I18N__, which is
   populated server-side by the push-reactivation-strings snippet rendered
   immediately before this script tag in theme.liquid. */

(function () {
  'use strict';

  var STORAGE_HIDE_KEY = 'hideNotificationBanner';
  var SESSION_HIDE_KEY = 'hideNotificationBannerSession';
  var HIDE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in ms — banner re-evaluates after this period since last dismiss.
  var BANNER_CLASS = 'notification-reactivation-banner';
  var DEBUG = false;

  // Feature detection. Exit silently if the runtime cannot support the banner.
  if (typeof window === 'undefined') return;
  if (typeof document === 'undefined') return;
  if (!('Notification' in window)) return;

  function log(message) {
    if (!DEBUG) return;
    try { console.info('[hola.push.reactivation] ' + message); } catch (e) { /* ignore */ }
  }

  // AC2/AC3 (updated by #8247): dismiss expires after HIDE_TTL_MS.
  // Returns true only if a valid numeric timestamp is stored AND it is younger
  // than HIDE_TTL_MS. Any other value (null, 'true' legacy, corrupt, expired)
  // returns false so the banner can render.
  function isBannerHidden() {
    try {
      var raw = window.localStorage.getItem(STORAGE_HIDE_KEY);
      if (raw === null || raw === '') return false;
      var n = parseInt(raw, 10);
      if (isNaN(n)) return false;
      return (Date.now() - n) < HIDE_TTL_MS;
    } catch (e) {
      // localStorage blocked (private mode, Safari ITP) — fail open.
      return false;
    }
  }

  // AC1 (updated by #8247): persist a numeric timestamp (ms since epoch),
  // NOT the literal 'true'. isBannerHidden() evaluates TTL against Date.now().
  function hideBannerPermanently() {
    try {
      window.localStorage.setItem(STORAGE_HIDE_KEY, String(Date.now()));
    } catch (e) {
      // Storage write failed (quota, private mode). The banner will hide
      // for this session via removeBanner(); next load it may reappear.
    }
  }

  // WI #8480: persist a session-scope flag so the Accept click suppresses the
  // banner for the rest of the tab's lifetime (cleared when the browser/tab
  // closes). Mirrors hideBannerPermanently() — fail-silent on storage error.
  function hideBannerForSession() {
    try {
      window.sessionStorage.setItem(SESSION_HIDE_KEY, '1');
      log('banner hidden for session');
    } catch (e) {
      // sessionStorage write failed (private mode, quota). Banner is still
      // removed for the current page; next navigation may re-render. This
      // mirrors hideBannerPermanently() fail-silent behaviour.
    }
  }

  // AC4 (#8247): users from release 2.0.32.0 have STORAGE_HIDE_KEY === 'true'
  // (the legacy boolean). Migrate them to a numeric timestamp at first load so
  // (a) isBannerHidden() returns true for them in this load (banner stays
  // hidden post-deploy) and (b) they get a fresh 30-day grace period before
  // the banner is re-evaluated.
  function migrateLegacyFlag() {
    try {
      var raw = window.localStorage.getItem(STORAGE_HIDE_KEY);
      if (raw === 'true') {
        window.localStorage.setItem(STORAGE_HIDE_KEY, String(Date.now()));
      }
    } catch (e) {
      // Storage inaccessible — nothing to migrate. isBannerHidden() will
      // fail-open and render the banner if permission is 'denied'.
    }
  }

  // WI #8480: read counterpart of hideBannerForSession(). Returns true when
  // the user clicked Accept earlier in this tab's session, so init() and
  // handlePermissionFlip() can short-circuit before re-rendering.
  function isBannerHiddenForSession() {
    try {
      return window.sessionStorage.getItem(SESSION_HIDE_KEY) === '1';
    } catch (e) {
      // sessionStorage blocked (private mode, Safari ITP) — fail open.
      return false;
    }
  }

  // WI #9077: detect the checkout / payment surface. On those pages this
  // module must NOT bind the visibilitychange/focus reload listeners at all,
  // because a window.location.reload() mid-PaymentIntent aborts the Stripe
  // 3D Secure / SCA challenge (the OTP step).
  //
  // Hola does NOT use Shopify's native checkout — the checkout/payment UI is
  // rendered by the storefront extension bundle (Shopify-Admin/extension-assets),
  // which marks the surface with sessionStorage['onCheckout']='yes' (set when the
  // payment form mounts in TransactionForm.tsx, cleared on leaving checkout, and
  // KEPT across reloads). We detect the surface with that SAME signal so this
  // guard fires on the real custom checkout. The Shopify-native checks below are
  // kept only as harmless fallbacks (they never fire for Hola).
  // Fail-safe: any error while probing returns true (suppress the reload
  // listeners) rather than risk reloading during payment.
  function isPaymentSurface() {
    try {
      // Primary signal — shared with extension-assets (the actual checkout owner).
      try {
        if (window.sessionStorage && window.sessionStorage.getItem('onCheckout') === 'yes') return true;
      } catch (e) { /* sessionStorage can throw in hardened privacy modes — ignore */ }

      // Fallbacks (no-ops for Hola's custom checkout; harmless if ever present).
      if (window.Shopify && window.Shopify.Checkout) return true;
      var path = '';
      try { path = (window.location && window.location.pathname) || ''; } catch (e) { path = ''; }
      if (/^\/(checkout|checkpoints)(\/|$)/.test(path)) return true;

      return false;
    } catch (e) {
      // Probing the surface failed — assume payment to stay safe (#9077).
      return true;
    }
  }

  // WI #9077: detect an active Stripe 3D Secure / SCA challenge. Stripe.js
  // injects challenge/authentication iframes whose name begins with
  // "__privateStripeFrame". While one is present (or the tab is not visible)
  // we must never reload — doing so aborts the in-flight PaymentIntent and
  // refreshes the page out from under the OTP input.
  function isStripeChallengeActive() {
    try {
      if (document.querySelector('iframe[name^="__privateStripeFrame"]')) return true;
      if (document.visibilityState && document.visibilityState !== 'visible') return true;
      return false;
    } catch (e) {
      // DOM probing failed — assume a challenge may be active to stay safe.
      return true;
    }
  }

  function detectBrowser() {
    var ua = '';
    try { ua = (window.navigator && window.navigator.userAgent) || ''; } catch (e) { ua = ''; }

    var isIOS = ua.indexOf('iPhone') !== -1 || ua.indexOf('iPad') !== -1 || ua.indexOf('iPod') !== -1;
    var isCriOS = ua.indexOf('CriOS') !== -1;
    var isFxiOS = ua.indexOf('FxiOS') !== -1;

    // Safari iOS: iOS device, Safari UA, not Chrome/Firefox on iOS.
    if (isIOS && ua.indexOf('Safari') !== -1 && !isCriOS && !isFxiOS) {
      return 'safari_ios';
    }

    // Edge must be checked before Chrome (its UA contains 'Chrome/').
    if (ua.indexOf('Edg/') !== -1 || ua.indexOf('Edge/') !== -1 || ua.indexOf('EdgiOS') !== -1) {
      return 'edge';
    }

    if (ua.indexOf('Firefox') !== -1 || isFxiOS) {
      return 'firefox';
    }

    // Desktop Safari: Safari UA, no Chrome/Chromium.
    if (ua.indexOf('Safari') !== -1 && ua.indexOf('Chrome') === -1 && ua.indexOf('Chromium') === -1) {
      return 'safari';
    }

    // WI #10397: Android Chrome gets its own steps (the desktop "lock icon" copy is wrong on a phone).
    if (ua.indexOf('Android') !== -1 && ua.indexOf('Chrome/') !== -1 &&
        ua.indexOf('Edg') === -1 && ua.indexOf('Firefox') === -1 && ua.indexOf('SamsungBrowser') === -1) {
      return 'android_chrome';
    }

    if (ua.indexOf('Chrome') !== -1 || isCriOS) {
      return 'chrome';
    }

    // Safe fallback — Chrome copy covers most blink-based browsers.
    return 'chrome';
  }

  // Shopify's `| t` filter HTML-escapes translated strings when Liquid is
  // rendered inside a <script> tag (e.g. "Don't" -> "Don&#39;t", ">" -> "&gt;").
  // We assign these values via textContent, which does not decode entities,
  // so we need to decode them ourselves before use.
  function decodeEntities(str) {
    if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
    var el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  }

  function decodeStringTree(node) {
    if (typeof node === 'string') return decodeEntities(node);
    if (node && typeof node === 'object') {
      var out = {};
      for (var k in node) {
        if (Object.prototype.hasOwnProperty.call(node, k)) {
          out[k] = decodeStringTree(node[k]);
        }
      }
      return out;
    }
    return node;
  }

  function getStrings() {
    var strings = null;
    try { strings = window.__HOLA_PUSH_REACTIVATION_I18N__ || null; } catch (e) { strings = null; }
    if (strings && typeof strings === 'object') return decodeStringTree(strings);
    // Minimal English fallback so the banner is never broken if the snippet
    // failed to render. Keys mirror the locale schema defined in T1.
    return {
      title: 'Notifications are blocked',
      // WI #10397: the booking confirmation always arrives by email; push only adds reminders.
      body: 'Your booking confirmation always arrives by email. Turn notifications on to get voucher and pickup reminders.',
      steps: {
        chrome: '',
        firefox: '',
        safari: '',
        safari_ios: '',
        edge: '',
        android_chrome: 'Tap the icon to the left of the address bar -> Permissions -> Notifications -> Allow'
      },
      cta_steps: 'How to turn them on',
      cta_dismiss: "Don't show again",
      cta_accept: 'Accept',
      aria: {
        dismiss_button: 'Close this notification reactivation banner',
        accept_button: 'Accept and close this notification reactivation banner'
      }
    };
  }

  function buildBanner(strings, browserKey) {
    var root = document.createElement('aside');
    root.className = BANNER_CLASS;
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label', strings.title || '');

    var titleEl = document.createElement('p');
    titleEl.className = BANNER_CLASS + '__title';
    titleEl.textContent = strings.title || '';
    root.appendChild(titleEl);

    var bodyEl = document.createElement('p');
    bodyEl.className = BANNER_CLASS + '__body';
    bodyEl.textContent = strings.body || '';
    root.appendChild(bodyEl);

    var stepsText = '';
    if (strings.steps && typeof strings.steps[browserKey] === 'string') {
      stepsText = strings.steps[browserKey];
    }
    // WI #10397: steps are collapsed behind a toggle (aria-expanded/aria-controls).
    // Neither element is rendered without steps text — the theme hides :empty globally.
    if (stepsText) {
      var toggleBtn = document.createElement('button');
      toggleBtn.type = 'button';
      toggleBtn.className = BANNER_CLASS + '__toggle';
      toggleBtn.setAttribute('aria-expanded', 'false');
      toggleBtn.setAttribute('aria-controls', 'hola-push-steps');
      toggleBtn.textContent = strings.cta_steps || 'How to turn them on';

      var stepsEl = document.createElement('p');
      stepsEl.id = 'hola-push-steps';
      stepsEl.className = BANNER_CLASS + '__steps';
      stepsEl.textContent = stepsText.split(' -> ').map(function(s) { return s.trim(); }).join(' / ');

      toggleBtn.addEventListener('click', function () {
        try {
          var openClass = BANNER_CLASS + '--steps-open';
          var open = root.className.indexOf(openClass) === -1;
          if (open) {
            root.className += ' ' + openClass;
          } else {
            root.className = root.className.replace(' ' + openClass, '');
          }
          toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        } catch (e) { /* ignore */ }
      });

      root.appendChild(toggleBtn);
      root.appendChild(stepsEl);
    }

    var actionsEl = document.createElement('div');
    actionsEl.className = BANNER_CLASS + '__actions';

    var dismissLink = document.createElement('button');
    dismissLink.type = 'button';
    dismissLink.className = BANNER_CLASS + '__link';
    dismissLink.textContent = strings.cta_dismiss || '';
    dismissLink.setAttribute('aria-label', (strings.aria && strings.aria.dismiss_button) || strings.cta_dismiss || '');
    dismissLink.addEventListener('click', function () {
      hideBannerPermanently();
      removeBanner();
    });
    actionsEl.appendChild(dismissLink);

    var acceptBtn = document.createElement('button');
    acceptBtn.type = 'button';
    acceptBtn.className = BANNER_CLASS + '__btn';
    acceptBtn.textContent = strings.cta_accept || '';
    acceptBtn.setAttribute('aria-label', (strings.aria && strings.aria.accept_button) || strings.cta_accept || '');
    acceptBtn.addEventListener('click', function () {
      hideBannerForSession();
      removeBanner();
    });
    actionsEl.appendChild(acceptBtn);

    root.appendChild(actionsEl);
    return root;
  }

  // WI #10397: the toast must never cover the hero search. While the
  // .banner__searchbar is in the viewport the toast stays in the DOM with
  // --hidden; it is shown once the searchbar scrolls out of view.
  var SEARCHBAR_SELECTOR = '.banner__searchbar';
  var HIDDEN_CLASS = BANNER_CLASS + '--hidden';
  var searchbarObserver = null;
  var scrollHandler = null;
  var scrollRafPending = false;
  var SCROLL_LISTENER_OPTS = { passive: true };

  // WI #10397: add/remove --hidden without duplicating it; logs state changes only.
  function setBannerHidden(node, hidden) {
    try {
      var padded = ' ' + node.className + ' ';
      var isHidden = padded.indexOf(' ' + HIDDEN_CLASS + ' ') !== -1;
      if (hidden === isHidden) return;
      if (hidden) {
        node.className += ' ' + HIDDEN_CLASS;
      } else {
        node.className = padded.replace(' ' + HIDDEN_CLASS + ' ', ' ').replace(/^\s+|\s+$/g, '');
      }
      log(hidden ? 'hidden while searchbar visible' : 'shown, searchbar out of view');
    } catch (e) { /* ignore */ }
  }

  // WI #10397: disconnect the observer / remove the scroll fallback.
  function teardownSearchbarWatch() {
    try {
      if (searchbarObserver) {
        searchbarObserver.disconnect();
        searchbarObserver = null;
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler, SCROLL_LISTENER_OPTS);
        scrollHandler = null;
        scrollRafPending = false;
      }
    } catch (e) { /* ignore */ }
  }

  // WI #10406: desktop = media-breakpoint-up(sm) (992px). There the toast keeps its
  // original bottom-centered placement and is not hidden with scroll — only mobile
  // had to give way to the hero search (WI #10397). If matchMedia is missing or throws, isDesktop() is false and the
  // mobile rule (the safe one) applies.
  var DESKTOP_MQ = '(min-width: 992px)';
  var desktopMql = null;
  var desktopMqlResolved = false;
  var desktopMqHandler = null;

  function getDesktopMql() {
    if (desktopMqlResolved) return desktopMql;
    desktopMqlResolved = true;
    try {
      if (typeof window.matchMedia === 'function') desktopMql = window.matchMedia(DESKTOP_MQ);
    } catch (e) { desktopMql = null; }
    return desktopMql;
  }

  function isDesktop() {
    try {
      var mql = getDesktopMql();
      return !!(mql && mql.matches);
    } catch (e) { return false; }
  }

  // WI #10406: one 'change' listener per rendered node; re-runs watchSearchbar when
  // the viewport crosses 992px. Own flag (desktopMqHandler): teardownSearchbarWatch()
  // runs on every watchSearchbar() call and must not remove it.
  function watchDesktopChange(node) {
    if (desktopMqHandler) return;
    var mql = getDesktopMql();
    if (!mql) return;
    var handler = function () {
      if (!node.parentNode) return;
      watchSearchbar(node);
    };
    try {
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', handler);
      } else if (typeof mql.addListener === 'function') {
        mql.addListener(handler); // Safari < 14
      } else {
        return;
      }
      desktopMqHandler = handler;
    } catch (e) { /* ignore */ }
  }

  // WI #10406: remove the matchMedia listener (called from removeBanner()).
  function teardownDesktopWatch() {
    if (!desktopMqHandler) return;
    try {
      var mql = getDesktopMql();
      if (mql) {
        if (typeof mql.removeEventListener === 'function') {
          mql.removeEventListener('change', desktopMqHandler);
        } else if (typeof mql.removeListener === 'function') {
          mql.removeListener(desktopMqHandler);
        }
      }
    } catch (e) { /* ignore */ }
    desktopMqHandler = null;
  }

  // WI #10397: tie the toast visibility to the hero searchbar. Without a
  // searchbar the toast stays visible (landings). If wiring throws, the toast
  // stays hidden — it must never show over the search.
  function watchSearchbar(node) {
    teardownSearchbarWatch();
    // WI #10406: on desktop the toast stays visible; no observer, no scroll listener.
    if (isDesktop()) {
      setBannerHidden(node, false);
      return;
    }
    var target = null;
    try { target = document.querySelector(SEARCHBAR_SELECTOR); } catch (e) { target = null; }
    if (!target) return;
    setBannerHidden(node, true);
    try {
      if (typeof IntersectionObserver !== 'undefined') {
        searchbarObserver = new IntersectionObserver(function (entries) {
          var e = entries[entries.length - 1];
          setBannerHidden(node, !!(e && e.isIntersecting));
        }, { threshold: 0 });
        searchbarObserver.observe(target);
      } else {
        var check = function () {
          scrollRafPending = false;
          var r = target.getBoundingClientRect();
          var visible = r.bottom > 0 && r.top < window.innerHeight;
          setBannerHidden(node, visible);
        };
        scrollHandler = function () {
          if (scrollRafPending) return;
          scrollRafPending = true;
          if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(check);
          } else {
            check();
          }
        };
        window.addEventListener('scroll', scrollHandler, SCROLL_LISTENER_OPTS);
      }
    } catch (e) {
      setBannerHidden(node, true);
      log('searchbar watch failed — banner kept hidden');
    }
  }

  // WI #10406: in the EEA the Pandectes cookie banner (.cc-window) opens on the
  // first visit; showing the toast at the same time stacks two pop-ups. The toast
  // gets --consent-hidden while that banner is open. Pandectes dispatches
  // OnShow/OnHide on window (premium plan); the DOM check covers a banner that
  // opened before this script ran. Own class so it never fights --hidden.
  var CONSENT_HIDDEN_CLASS = BANNER_CLASS + '--consent-hidden';
  var consentBannerOpen = false;

  function isConsentBannerInDom() {
    try {
      // A closed banner stays in the DOM with display:'' + cc-invisible (measured in prod),
      // so both are checked. A fade-in still in progress is covered by OnShow.
      var w = document.querySelector('.cc-window');
      if (!w || w.style.display === 'none') return false;
      return (' ' + w.className + ' ').indexOf(' cc-invisible ') === -1;
    } catch (e) { return false; }
  }

  function setConsentHidden(node, hidden) {
    try {
      var padded = ' ' + node.className + ' ';
      var isHidden = padded.indexOf(' ' + CONSENT_HIDDEN_CLASS + ' ') !== -1;
      if (hidden === isHidden) return;
      if (hidden) {
        node.className += ' ' + CONSENT_HIDDEN_CLASS;
      } else {
        node.className = padded.replace(' ' + CONSENT_HIDDEN_CLASS + ' ', ' ').replace(/^\s+|\s+$/g, '');
      }
      log(hidden ? 'hidden while cookie banner open' : 'shown, cookie banner closed');
    } catch (e) { /* ignore */ }
  }

  function onConsentBanner(open) {
    consentBannerOpen = open;
    var node = document.querySelector('.' + BANNER_CLASS);
    if (node) setConsentHidden(node, open);
  }

  try {
    window.addEventListener('PandectesEvent_OnShow', function () { onConsentBanner(true); });
    window.addEventListener('PandectesEvent_OnHide', function () { onConsentBanner(false); });
  } catch (e) { /* ignore */ }

  function renderBanner() {
    // Guard against double-render (e.g., if init() is invoked twice).
    if (document.querySelector('.' + BANNER_CLASS)) return;
    var strings = getStrings();
    var browserKey = detectBrowser();
    var node = buildBanner(strings, browserKey);
    if (document.body) {
      // WI #10406: never on top of the cookie banner.
      if (consentBannerOpen || isConsentBannerInDom()) setConsentHidden(node, true);
      // WI #10397: start hidden when the searchbar exists so there is no
      // one-frame flash over it; watchSearchbar() then drives visibility.
      // WI #10406: only below 992px; on desktop it is never hidden.
      var hasSearchbar = false;
      try { hasSearchbar = !!document.querySelector(SEARCHBAR_SELECTOR); } catch (e) { hasSearchbar = false; }
      if (hasSearchbar && !isDesktop()) setBannerHidden(node, true);
      document.body.appendChild(node);
      watchSearchbar(node);
      watchDesktopChange(node); // WI #10406
      log('banner rendered for ' + browserKey);
    }
  }

  function removeBanner() {
    // WI #10397: always tear down the searchbar watch, even if no node exists.
    teardownSearchbarWatch();
    teardownDesktopWatch(); // WI #10406: drop the matchMedia 'change' listener too.
    var node = document.querySelector('.' + BANNER_CLASS);
    if (node && node.parentNode) {
      node.parentNode.removeChild(node);
      log('banner removed');
    }
  }

  // WI #9077: guard so the granted-flip reload fires at most once and never
  // re-enters after we have already scheduled/executed a reload for this tab.
  var reloadDone = false;

  function handlePermissionFlip() {
    var permission = 'default';
    try { permission = Notification.permission; } catch (e) { return; }

    if (permission === 'granted') {
      // User enabled notifications via browser settings. Remove the banner
      // and reload so firebase-push-client.js picks up the new permission
      // state on its normal init path and registers the FCM token. We do
      // NOT call into that module directly — keeping it closed avoids
      // coupling this banner to its internal flow.
      removeBanner();

      // WI #9077: never reload while on the checkout/payment surface, while a
      // Stripe 3D Secure / SCA challenge is active, or while the tab is not
      // visible — a mid-PaymentIntent reload aborts the OTP step. The
      // isPaymentSurface() check (sessionStorage onCheckout) covers the whole
      // checkout session, closing the gap where the Stripe iframe is briefly
      // detached (e.g. mobile bank-app hand-off) but payment is still in flight.
      // The reload is retried on the next visibilitychange -> visible once the
      // user has left checkout.
      if (reloadDone) return;
      if (isPaymentSurface() || isStripeChallengeActive()) {
        log('granted flip detected but checkout/3DS active — skipping reload');
        return;
      }
      reloadDone = true;
      try { window.location.reload(); } catch (e) { /* ignore */ }
      return;
    }

    if (permission === 'denied') {
      // Permission is still denied. If the user hasn't dismissed the
      // banner permanently, make sure it is rendered (covers the edge
      // case where the banner was removed by other code).
      // WI #10406: not while the cookie banner is still waiting for a decision.
      if (consentSettled && !isBannerHidden() && !isBannerHiddenForSession() && !document.querySelector('.' + BANNER_CLASS)) {
        renderBanner();
      }
    }
  }

  // WI #10406: in the EU the Pandectes cookie banner (a centered modal over a dark
  // overlay) opens on the same load. Both at once read as a double pop-up, so this
  // toast waits until the visitor has decided on cookies. Pandectes signals:
  //   - getCurrentConsent(): { applicable, status } — applicable && !status means
  //     its banner is still waiting for a decision; null when its API is off.
  //   - window events PandectesEvent_OnConsent (detail.consentType: 'default' on
  //     every load without a decision, 'new' / 'stored' / 'revoke' otherwise),
  //     PandectesEvent_OnInitialize, PandectesEvent_OnHide, PandectesEvent_OnClose.
  // No Pandectes on the page, or no answer within CONSENT_WAIT_MS while it is not
  // known to be pending: render as before.
  var CONSENT_WAIT_MS = 6000;
  var consentSettled = false;

  function isConsentBannerPending() {
    try {
      var p = window.Pandectes;
      if (!p || typeof p.getCurrentConsent !== 'function') return null; // not loaded yet
      var current = p.getCurrentConsent();
      if (!current) return false; // Pandectes API disabled
      return current.applicable === true && !current.status;
    } catch (e) {
      return false;
    }
  }

  function hasPandectesOnPage() {
    try {
      return !!window.Pandectes || !!document.querySelector('script[src*="pandectes"]');
    } catch (e) {
      return false;
    }
  }

  function whenConsentSettled(callback) {
    var done = false;
    var timer = null;
    var events = ['PandectesEvent_OnInitialize', 'PandectesEvent_OnConsent', 'PandectesEvent_OnHide', 'PandectesEvent_OnClose'];

    function settle(reason) {
      if (done) return;
      done = true;
      consentSettled = true;
      if (timer) clearTimeout(timer);
      for (var i = 0; i < events.length; i++) window.removeEventListener(events[i], onEvent);
      log('consent settled (' + reason + ')');
      callback();
    }

    function onEvent(e) {
      var type = e && e.type;
      if (type === 'PandectesEvent_OnHide' || type === 'PandectesEvent_OnClose') {
        settle('cookie banner closed');
        return;
      }
      var detail = e && e.detail;
      if (type === 'PandectesEvent_OnConsent' && detail && detail.consentType && detail.consentType !== 'default') {
        settle('consent ' + detail.consentType);
        return;
      }
      if (isConsentBannerPending() === false) settle('no cookie banner pending');
    }

    if (!hasPandectesOnPage() || isConsentBannerPending() === false) {
      settle('no cookie banner pending');
      return;
    }
    for (var i = 0; i < events.length; i++) window.addEventListener(events[i], onEvent);
    timer = setTimeout(function () {
      timer = null;
      // Still waiting for the visitor: keep listening, the decision events will come.
      if (isConsentBannerPending() === true) return;
      settle('timeout');
    }, CONSENT_WAIT_MS);
    log('waiting for the cookie banner decision');
  }

  function canRender() {
    var permission = 'default';
    try { permission = Notification.permission; } catch (e) { return false; }
    return permission === 'denied' && !isBannerHiddenForSession() && !isBannerHidden();
  }

  function init() {
    migrateLegacyFlag();
    var permission = 'default';
    try { permission = Notification.permission; } catch (e) { return; }

    // AC6: only render when permission is explicitly 'denied'. For
    // 'granted' or 'default', the banner must not exist in the DOM.
    if (permission !== 'denied') return;

    // WI #8480: Accept persists a session-scope suppression flag in sessionStorage.
    if (isBannerHiddenForSession()) return;

    // AC2 (updated by #8247): dismiss expires after HIDE_TTL_MS; see isBannerHidden() for TTL logic.
    if (isBannerHidden()) return;

    // WI #10406: wait for the Pandectes cookie decision, then re-check the flags.
    whenConsentSettled(function () {
      if (canRender()) renderBanner();
    });

    // WI #9077: on the checkout/payment surface do NOT bind the reload
    // listeners at all. A visibilitychange/focus-triggered reload there
    // aborts the in-flight Stripe 3D Secure / SCA PaymentIntent (the OTP
    // step). The banner stays rendered but inert on those pages.
    if (isPaymentSurface()) {
      log('payment surface detected — reload listeners not bound (#9077)');
      return;
    }

    // AC3: re-evaluate permission on tab visibility change so a
    // denied -> granted flip made via the browser UI is picked up. WI #9077:
    // only react when the tab becomes visible (never on a bare window focus,
    // which fires while a Stripe challenge iframe still holds the page), and
    // debounce so a focus/visibility storm cannot fire repeated reloads.
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  // WI #9077: visibilitychange handler. Only forwards to handlePermissionFlip
  // when the document is actually visible, so the granted-flip reload happens
  // on a deliberate return to the tab rather than on transient focus events
  // raised while a Stripe 3DS challenge iframe is open.
  var visibilityDebounce = null;
  function onVisibilityChange() {
    if (document.visibilityState !== 'visible') return;
    if (visibilityDebounce) return;
    visibilityDebounce = setTimeout(function () {
      visibilityDebounce = null;
      handlePermissionFlip();
    }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
